import React, { useRef, useEffect, useState } from 'react';
import Hls from 'hls.js';

const HLSVideoPlayer = ({ src, autoPlay = true, loop = false, muted = true, controls = false, onReady, onError }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  // Hide the <video> until it's actually playing. The wrapper has bg:#000 so the user sees
  // pure black instead of the browser's native play-button overlay.
  const [isPlaying, setIsPlaying] = useState(false);

  // Reset visibility whenever src changes so we don't briefly flash the previous frame.
  useEffect(() => { setIsPlaying(false); }, [src]);

  // Wire native playback events to drive the visibility state.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlaying = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEmptied = () => setIsPlaying(false);
    v.addEventListener('playing', onPlaying);
    v.addEventListener('pause', onPause);
    v.addEventListener('emptied', onEmptied);
    return () => {
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('emptied', onEmptied);
    };
  }, []);

  useEffect(() => {
    if (!src) return;

    const video = videoRef.current;
    if (!video) return;

    const cleanup = () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };

    if (Hls.isSupported()) {
      // Use HLS.js for browsers that don't support HLS natively
      cleanup();
      
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5,
        // Updated configuration using new API (v1.x.x)
        xhrSetup: function(xhr, url) {
          // Add any custom headers if needed
          // xhr.setRequestHeader('Authorization', 'Bearer token');
        },
        // Manifest loading policy
        manifestLoadPolicy: {
          default: {
            maxTimeToFirstByteMs: 10000,
            maxLoadTimeMs: 10000,
            timeoutRetry: {
              maxNumRetry: 6,
              retryDelayMs: 500,
              maxRetryDelayMs: 0
            },
            errorRetry: {
              maxNumRetry: 6,
              retryDelayMs: 500,
              maxRetryDelayMs: 8000
            }
          }
        },
        // Playlist loading policy
        playlistLoadPolicy: {
          default: {
            maxTimeToFirstByteMs: 10000,
            maxLoadTimeMs: 10000,
            timeoutRetry: {
              maxNumRetry: 6,
              retryDelayMs: 500,
              maxRetryDelayMs: 0
            },
            errorRetry: {
              maxNumRetry: 6,
              retryDelayMs: 500,
              maxRetryDelayMs: 8000
            }
          }
        },
        // Fragment loading policy
        fragLoadPolicy: {
          default: {
            maxTimeToFirstByteMs: 20000,
            maxLoadTimeMs: 20000,
            timeoutRetry: {
              maxNumRetry: 6,
              retryDelayMs: 500,
              maxRetryDelayMs: 0
            },
            errorRetry: {
              maxNumRetry: 6,
              retryDelayMs: 500,
              maxRetryDelayMs: 8000
            }
          }
        },
        enableSoftwareAES: true,
        startFragPrefetch: true,
        // Enable debug logging in development
        debug: process.env.NODE_ENV === 'development',
      });

      hlsRef.current = hls;

      hls.loadSource(src);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('HLS manifest parsed, attempting to play');
        if (autoPlay) {
          video.play().catch(err => {
            console.log('Auto-play prevented:', err);
            // Try muted autoplay if unmuted fails
            if (!muted) {
              video.muted = true;
              video.play().catch(err2 => {
                console.log('Muted autoplay also prevented:', err2);
                onError?.(err2);
              });
            } else {
              onError?.(err);
            }
          });
        }
        onReady?.();
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data);
        
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('Network error (403 Forbidden?), trying to recover');
              // First, check if it's a 403 error
              if (data.response && data.response.code === 403) {
                console.log('403 Forbidden error - file not publicly accessible');
                onError?.(new Error('403 Forbidden - Check S3 permissions'));
                
                // Try to fallback to MP4 if this is an HLS URL
                if (src.includes('.m3u8')) {
                  const mp4Url = src.replace('.m3u8', '.mp4').replace('/master.m3u8', '/video.mp4');
                  console.log('Attempting fallback to MP4:', mp4Url);
                  // You might want to trigger a fallback here
                }
              } else {
                // Try to recover from other network errors
                hls.startLoad();
              }
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('Media error, trying to recover');
              hls.recoverMediaError();
              break;
            default:
              console.log('Fatal HLS error, cannot recover');
              cleanup();
              onError?.(new Error('Fatal HLS error: ' + data.details));
              break;
          }
        }
      });

      // Add network error handler specifically
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.details === 'manifestLoadError' && data.response?.code === 403) {
          console.error('403 Forbidden accessing HLS manifest');
          console.error('URL:', data.url);
          console.error('Possible causes:');
          console.error('1. S3 bucket not public');
          console.error('2. CloudFront not configured properly');
          console.error('3. CORS issues');
        }
      });

    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari and other native HLS support
      video.src = src;
      
      // Add error listener for native playback
      const errorHandler = (e) => {
        console.error('Native HLS error:', e);
        // Check for 403 in network requests
        if (e.target.error && e.target.error.code === 4) { // MEDIA_ERR_NETWORK
          console.error('Network error - possibly 403 Forbidden');
          onError?.(new Error('Network error - Check S3 permissions'));
        } else {
          onError?.(e);
        }
      };
      
      video.addEventListener('error', errorHandler);
      
      video.addEventListener('loadedmetadata', () => {
        if (autoPlay) {
          video.play().catch(err => {
            console.log('Auto-play prevented:', err);
            onError?.(err);
          });
        }
        onReady?.();
      });
      
      // Return cleanup function
      return () => {
        video.removeEventListener('error', errorHandler);
      };
    } else {
      console.error('HLS not supported');
      onError?.(new Error('HLS not supported'));
    }

    return cleanup;
  }, [src, autoPlay, muted, onReady, onError]);

  return (
    <video
      ref={videoRef}
      className="hls-video"
      autoPlay={autoPlay}
      loop={loop}
      muted={muted}
      controls={controls}
      playsInline
      preload="auto"
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
        // Hide until first 'playing' event — wrapper's black bg shows through.
        // This kills the native play-overlay icon that appears between mount and playback.
        visibility: isPlaying ? "visible" : "hidden",
        opacity: isPlaying ? 1 : 0,
        transition: "opacity 0.15s ease-in"
      }}
    />
  );
};

export default HLSVideoPlayer;